import React, { useState } from 'react';
import { Eye, EyeOff, Trash2, Edit, BookOpen, SearchIcon, X, FolderOpen } from 'lucide-react';
import { Button, Input, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Guide } from '@/services/apis/guides';
import { BaseTable, TableColumn, ProductSelector, StatusSelector } from '../../base';
import CategorySelector from '../../base/selectors/CategorySelector';
import { BaseTableCell } from '../../base/BaseTable';
import CreateGuideButton from './CreateGuideButton';
import GuidesStatsCards from './GuidesStatsCards';
import ConfirmationModal from '../../base/ConfirmationModal';
import CategoriesModal from '../CategoriesModal';

interface GuideTabProps {
  guides: Guide[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedProductId?: string;
  onProductChange: (value: string | undefined) => void;
  selectedCategoryId?: string;
  onCategoryChange: (value: string | undefined) => void;
  selectedStatus?: string;
  onStatusChange: (value: string | undefined) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onEdit: (guide: Guide) => void;
  onDelete: (guide: Guide) => void;
  onPublish: (guide: Guide) => void;
  onUnpublish: (guide: Guide) => void;
  deleteModalOpen: boolean;
  publishModalOpen: boolean;
  unpublishModalOpen: boolean;
  selectedGuide: Guide | null; // Used in modals for confirmation messages
  onDeleteConfirm: () => void;
  onPublishConfirm: () => void;
  onUnpublishConfirm: () => void;
  onDeleteModalClose: () => void;
  onPublishModalClose: () => void;
  onUnpublishModalClose: () => void;
  isDeleting: boolean;
  isPublishing: boolean;
  isUnpublishing: boolean;
  loadMoreRef: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  hasNextPage: boolean;
}

const GuidesTab: React.FC<GuideTabProps> = ({
  guides,
  isLoading,
  isFetchingNextPage,
  error,
  searchQuery,
  onSearchChange,
  selectedProductId,
  onProductChange,
  selectedCategoryId,
  onCategoryChange,
  selectedStatus,
  onStatusChange,
  hasActiveFilters,
  onClearFilters,
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
  deleteModalOpen,
  publishModalOpen,
  unpublishModalOpen,
  selectedGuide: _selectedGuide,
  onDeleteConfirm,
  onPublishConfirm,
  onUnpublishConfirm,
  onDeleteModalClose,
  onPublishModalClose,
  onUnpublishModalClose,
  isDeleting,
  isPublishing,
  isUnpublishing,
  loadMoreRef,
  hasNextPage,
}) => {
  const { t } = useTranslation('admin');
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

  const guideColumns: TableColumn[] = [
    { name: t('guides.table.columns.title'), uid: 'title', sortable: true },
    { name: t('guides.table.columns.product'), uid: 'product', sortable: true },
    { name: t('guides.table.columns.difficulty'), uid: 'difficulty', sortable: true },
    { name: t('guides.table.columns.duration'), uid: 'estimatedTime', sortable: true },
    { name: t('guides.table.columns.steps'), uid: 'steps', sortable: true },
    { name: t('guides.table.columns.status'), uid: 'isPublished', sortable: true },
    { name: t('guides.table.columns.actions'), uid: 'actions', sortable: false },
  ];

  const renderCell = (guide: Guide, columnKey: string) => {
    const translation = guide.translations?.find((t) => t.language === 'en') || guide.translations?.[0];
    const title = translation?.title || '';
    const description = translation?.description || '';

    switch (columnKey) {
      case 'title':
        return <BaseTableCell text={title} description={description} />;

      case 'product':
        return <BaseTableCell text={guide.product?.name || t('guides.placeholders.noProduct')} />;

      case 'difficulty':
        return <BaseTableCell text={guide.difficulty ? t(`guides.difficulty.${guide.difficulty}`) : ''} />;

      case 'estimatedTime':
        return (
          <BaseTableCell
            text={
              guide.estimatedTime
                ? `${guide.estimatedTime} ${t('guides.cells.minutes')}`
                : t('guides.placeholders.notSpecified')
            }
          />
        );

      case 'steps':
        return <BaseTableCell text={`${guide.steps?.length || 0} ${t('guides.cells.steps')}`} />;

      case 'isPublished':
        return <BaseTableCell text={guide.isPublished ? t('common.status.published') : t('common.status.draft')} />;

      case 'actions':
        return (
          <div className="flex items-center space-x-2">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => (guide.isPublished ? onUnpublish(guide) : onPublish(guide))}
              aria-label={guide.isPublished ? t('guides.actions.unpublish') : t('guides.actions.publish')}
              title={guide.isPublished ? t('guides.actions.unpublish') : t('guides.actions.publish')}
            >
              {guide.isPublished ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onPress={() => onEdit(guide)}
              aria-label={t('guides.actions.edit')}
              title={t('guides.actions.edit')}
            >
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onPress={() => onDelete(guide)}
              aria-label={t('guides.actions.delete')}
              title={t('guides.actions.delete')}
              isDisabled={guide.isPublished}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('guides.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('guides.subtitle')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <GuidesStatsCards />

      {/* Controls */}

      <div className="flex justify-end gap-3">
        <Button
          variant="flat"
          startContent={<FolderOpen className="w-4 h-4" />}
          onPress={() => setIsCategoriesModalOpen(true)}
        >
          {t('guides.categories.manageButton')}
        </Button>
        <CreateGuideButton />
      </div>
      <div className="flex justify-between gap-3 items-end flex-wrap">
        <Input
          isClearable
          className="w-full sm:max-w-[44%]"
          placeholder={t('guides.search.placeholder')}
          startContent={<SearchIcon />}
          value={searchQuery}
          onClear={() => onSearchChange('')}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3 flex-wrap">
          <div className="w-[150px]">
            <StatusSelector
              value={selectedStatus || 'all'}
              onChange={(value) => onStatusChange(value === 'all' ? undefined : value)}
              options={[
                { key: 'published', label: t('common.status.published') },
                { key: 'draft', label: t('common.status.draft') },
              ]}
              placeholder={t('guides.table.columns.status')}
              variant="flat"
              label="Status"
              showAllOption={true}
            />
          </div>
          <div className="w-[150px]">
            <ProductSelector
              value={selectedProductId || 'all'}
              onChange={(value) => onProductChange(value === 'all' ? undefined : value)}
              placeholder={t('guides.table.columns.product')}
              variant="flat"
              label="Products"
              showAllOption={true}
            />
          </div>
          <div className="w-[150px]">
            <CategorySelector
              value={selectedCategoryId || 'all'}
              onChange={(value) => {
                const categoryValue = Array.isArray(value) ? value[0] : value;
                onCategoryChange(categoryValue === 'all' ? undefined : categoryValue);
              }}
              placeholder={t('guides.table.columns.category')}
              variant="flat"
              label="Categories"
              showAllOption={true}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="flat" onPress={onClearFilters} startContent={<X className="w-4 h-4" />}>
              {t('guides.filters.clear')}
            </Button>
          )}
        </div>
      </div>
      {/* Guides Table */}
      <BaseTable
        data={guides}
        columns={guideColumns}
        isLoading={isLoading && guides.length === 0}
        loadingMessage={t('guides.table.loading')}
        error={error}
        emptyMessage={t('guides.table.empty.title')}
        emptyIcon={<BookOpen className="w-12 h-12 text-gray-400" />}
        renderCell={renderCell}
        stickyHeader={true}
        hoverable={true}
        striped={true}
        idField="id"
      />

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner size="lg" />}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={onDeleteConfirm}
        title={t('guides.modals.delete.title')}
        message={t('guides.modals.delete.message')}
        confirmLabel={t('guides.modals.delete.confirm')}
        cancelLabel={t('guides.modals.delete.cancel')}
        confirmColor="danger"
        isLoading={isDeleting}
      />

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        isOpen={publishModalOpen}
        onClose={onPublishModalClose}
        onConfirm={onPublishConfirm}
        title={t('guides.modals.publish.title')}
        message={t('guides.modals.publish.message')}
        confirmLabel={t('guides.modals.publish.confirm')}
        cancelLabel={t('guides.modals.publish.cancel')}
        confirmColor="primary"
        isLoading={isPublishing}
      />

      {/* Unpublish Confirmation Modal */}
      <ConfirmationModal
        isOpen={unpublishModalOpen}
        onClose={onUnpublishModalClose}
        onConfirm={onUnpublishConfirm}
        title={t('guides.modals.unpublish.title')}
        message={t('guides.modals.unpublish.message')}
        confirmLabel={t('guides.modals.unpublish.confirm')}
        cancelLabel={t('guides.modals.unpublish.cancel')}
        confirmColor="warning"
        isLoading={isUnpublishing}
      />

      {/* Categories Management Modal */}
      <CategoriesModal isOpen={isCategoriesModalOpen} onClose={() => setIsCategoriesModalOpen(false)} />
    </div>
  );
};

export default GuidesTab;
